<script setup>
import UiDialog from '@/components/ui/UiDialog.vue';
import UiBadge from '@/components/ui/UiBadge.vue';

// A student at risk: where they are and every subject with a low grade.
// `actionable` (teacher dashboard) also shows each subject's recommendation
// and the action taken, with a button to log one.
defineProps({
  student: { type: Object, default: null },
  actionable: { type: Boolean, default: false },
});
defineEmits(['log-action']);
const open = defineModel({ type: Boolean, default: false });
</script>

<template>
  <UiDialog
    v-model="open"
    :title="student?.name || 'Student'"
    :description="student?.lrn ? `LRN ${student.lrn}` : ''"
    size="md"
    :persistent="false"
  >
    <div v-if="student" class="details">
      <dl class="facts">
        <div>
          <dt>Grade level</dt>
          <dd>{{ student.gradeLevel || '—' }}</dd>
        </div>
        <div>
          <dt>Section</dt>
          <dd>{{ student.section ? student.section.toUpperCase() : '—' }}</dd>
        </div>
        <div>
          <dt>Adviser</dt>
          <dd>{{ student.adviser || '—' }}</dd>
        </div>
      </dl>

      <div>
        <h3 class="details__heading">
          {{ student.subjects.length }}
          {{ student.subjects.length === 1 ? 'subject' : 'subjects' }} below 80
        </h3>
        <ul class="subjects">
          <li v-for="subject in student.subjects" :key="subject.atriskID ?? subject.title" class="subjects__item">
            <div class="subjects__row">
              <span class="subjects__text">
                <span class="subjects__title">{{ subject.title }}</span>
                <span v-if="subject.teacher" class="subjects__teacher">{{ subject.teacher }}</span>
              </span>
              <UiBadge :variant="subject.risk.variant">{{ subject.grade ?? '—' }} · {{ subject.risk.label }}</UiBadge>
            </div>
            <div v-if="actionable" class="subjects__action">
              <span v-if="subject.remarks" class="subjects__teacher">
                Recommended: {{ subject.remarks }}
              </span>
              <span class="subjects__done">
                <span :class="{ 'subjects__teacher': !subject.action_taken }">
                  {{ subject.action_taken || 'No action logged yet.' }}
                </span>
                <v-btn
                  variant="text"
                  size="small"
                  prepend-icon="mdi-pencil-outline"
                  @click="$emit('log-action', subject)"
                  >{{ subject.action_taken ? 'Edit action' : 'Log action' }}</v-btn
                >
              </span>
            </div>
          </li>
        </ul>
      </div>
    </div>

    <template #footer>
      <v-btn variant="outlined" @click="open = false">Close</v-btn>
    </template>
  </UiDialog>
</template>

<style scoped>
.details {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.facts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin: 0;
}
.facts dt {
  font-size: 12px;
  color: var(--muted-foreground);
}
.facts dd {
  margin: 2px 0 0;
  font-size: 14px;
  font-weight: 500;
}
.details__heading {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 600;
}
.subjects {
  margin: 0;
  padding: 0;
  list-style: none;
  border: 1px solid var(--border);
  border-radius: 10px;
}
.subjects__item {
  padding: 10px 12px;
}
.subjects__item + .subjects__item {
  border-top: 1px solid var(--border);
}
.subjects__row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.subjects__action {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;
  font-size: 13px;
}
.subjects__done {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.subjects__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
}
.subjects__title {
  font-size: 14px;
  font-weight: 500;
}
.subjects__teacher {
  font-size: 12px;
  color: var(--muted-foreground);
}
@media (max-width: 600px) {
  .facts {
    grid-template-columns: 1fr;
  }
}
</style>
